import {
  SignInOutput,
  GetCurrentUserOutput,
  signOut,
  signIn,
  fetchAuthSession
} from 'aws-amplify/auth';
import { tokenRefreshManager } from './tokenRefreshManager';
import { handleLogout } from 'store/auth/thunks';
import * as Sentry from '@sentry/react';
import { isNetworkError } from 'utils/networkErrorUtils';

const TIME_SYNC_THRESHOLD = 5 * 60 * 1000;

async function checkTimeSync(): Promise<boolean> {
  try {
    const response = await fetch('https://worldtimeapi.org/api/timezone/UTC', {
      method: 'GET',
      cache: 'no-cache'
    });
    
    if (!response.ok) {
      console.warn('Time sync check failed, proceeding with local time');
      return true;
    }
    
    const data = await response.json();
    const serverTime = new Date(data.utc_datetime).getTime();
    const localTime = Date.now();
    const timeDiff = Math.abs(serverTime - localTime);
    
    if (timeDiff > TIME_SYNC_THRESHOLD) {
      console.warn(`Time drift detected: ${timeDiff}ms difference from server time`);
      return false;
    }
    
    return true;
  } catch (error) {
    console.warn('Time sync check failed, proceeding with local time:', error);
    return true;
  }
}

interface LoginCredentials {
  username: string;
  password: string;
}
type ErrorCallback = (error: string) => void;
// export async function handleAwsLogin({
//   username,
//   password,
// }: LoginCredentials): Promise<SignInOutput | GetCurrentUserOutput | undefined> {
//   try {
//     const user: AuthUser = await getCurrentUser();
//     return user;
//   } catch (error: any) {
//     if (error.name === 'UserNotAuthenticatedException' || error.name=='UserUnAuthenticatedException') {
//       try {
//         const signInResult: SignInOutput = await signIn({ username, password });
//         console.log('Signed in successfully:', signInResult.isSignedIn);
//         return signInResult;
//       } catch (signInError) {
//         console.error('Sign-in failed:', signInError);
//         return undefined;
//       }
//     } else {
//       console.error('Unexpected error checking user status:', error);
//       return undefined;
//     }
//   }
// }

const isTokenExpired = (exp: number): boolean => {
  if (!exp) return true;
  const currentTime = Math.floor(Date.now() / 1000);
  return exp <= currentTime;
}

export async function handleAwsLogin({
  username,
  password,
  }: LoginCredentials , errorCallback?: ErrorCallback): Promise<SignInOutput | GetCurrentUserOutput | undefined> {
  try {
    try {
      const session = await fetchAuthSession();
      if (session.tokens && session.tokens.accessToken) {
        const accessTokenExp = session.tokens.accessToken.payload?.exp;
        const idTokenExp = session.tokens.idToken?.payload?.exp;
        
        const isAccessTokenValid = accessTokenExp && !isTokenExpired(accessTokenExp);
        const isIdTokenValid = idTokenExp && !isTokenExpired(idTokenExp);
        
        if (isAccessTokenValid && isIdTokenValid) {
          console.log('User already signed in with valid tokens, skipping sign-in');
          return { isSignedIn: true } as SignInOutput;
        } else {
          console.log('Tokens expired, proceeding with sign-in');
        }
      }
    } catch (sessionError) {
      console.log('No session found, signing in', sessionError);
    }
    
    await signOut();
    const signInResult: SignInOutput = await signIn({ username, password });
    console.log('Signed in successfully:', signInResult.isSignedIn);
    return signInResult;
  } catch (error: any) {
    console.error('Sign-in failed:', error.message || error.name);
    if(errorCallback){
      errorCallback(error.name);
    }
    return undefined;
  }
}





export async function checkAuth({ currentUser, dispatch, navigate }) {
  const maxRetries = 3;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const sessionInfo = await tokenRefreshManager.getValidSession();
      return;
    } catch (error) {
      if (isNetworkError(error)) {
        return;
      }

      const message = error?.message || error?.name;
      const errorName = error?.name || '';

      const isSignatureExpired = message?.includes('Signature expired') || 
                                message?.includes('Clock drift') ||
                                message === 'SignatureExpired';

      if (isSignatureExpired && retryCount < maxRetries - 1) {
        retryCount++;
        console.log(`Signature expired in checkAuth, retrying... (${retryCount}/${maxRetries})`);
        Sentry.captureMessage(`174 Clock drift detected in checkAuth, retry ${retryCount}/${maxRetries}: ${message}`, 'warning');
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        continue;
      }

      const isNotAuthenticatedCase =
        message === 'NotAuthenticated' ||
        message === 'Not authenticated' ||
        errorName === 'NotAuthorizedException' ||
        errorName === 'UserUnAuthenticatedException' ||
        errorName === 'UserNotAuthenticatedException' ||
        (typeof message === 'string' && message.toLowerCase().includes('not authenticated'));

      const isSessionExpiredCase =
        message === 'SessionExpired' ||
        message === 'Session expired' ||
        errorName === 'TokenExpiredError' ||
        (typeof message === 'string' &&
          (message.toLowerCase().includes('session expired') ||
            message.toLowerCase().includes('refresh token')));

      if (isNotAuthenticatedCase) {
        if (currentUser?.id) {
          dispatch(handleLogout());
        } else {
          navigate('/login');
        }
      } else if (isSessionExpiredCase) {
        if (currentUser?.id) {
          try {
            await tokenRefreshManager.refreshToken(true);
          } catch {
            dispatch(handleLogout());
          }
        } else {
          navigate('/login');
        }
      } else {
        Sentry.captureException(error instanceof Error ? error : new Error(String(message)), {
          tags: { errorType: 'checkAuth_unclassified' },
          extra: {
            errorName,
            message,
            hasCurrentUser: !!currentUser?.id
          },
          level: 'warning'
        });
      }
      break;
    }
  }
}